import { Platform, Image as RNImage } from "react-native";
import * as ImageManipulator from "expo-image-manipulator";

export interface IResizeImageOptions {
  width: number;
  height: number;
  mimeType?: "image/jpeg" | "image/png" | "image/webp";
  quality?: number;
}

export interface IProfileImageVariants {
  photoBase64: string;
  iconBase64: string;
  contentType: string;
}

/**
 * Loads an image from a File, Blob, or URL string into an HTMLImageElement.
 *
 * @param {File | Blob | string} source - The source image.
 * @returns {Promise<HTMLImageElement>} Loaded HTMLImageElement.
 */
function loadImage(source: File | Blob | string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    if (!source) {
      reject(new Error("Source image must be provided."));
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      resolve(img);
    };

    img.onerror = () => {
      reject(new Error("Failed to load image for resizing."));
    };

    if (typeof source === "string") {
      img.src = source;
    } else {
      const objectUrl = URL.createObjectURL(source);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Failed to load image file."));
      };
      img.src = objectUrl;
    }
  });
}

/**
 * Resizes and center-crops an image onto an HTMLCanvasElement to exact dimensions.
 *
 * @param {HTMLImageElement} img - The source image element.
 * @param {IResizeImageOptions} options - Desired width, height, mimeType, and quality.
 * @returns {string} Data URL of the resized and cropped image.
 */
export function cropAndResizeImage(
  img: HTMLImageElement,
  options: IResizeImageOptions
): string {
  const { width, height, mimeType = "image/jpeg", quality = 0.85 } = options;

  if (width <= 0 || height <= 0) {
    throw new Error("Target dimensions must be strictly positive.");
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas 2D context is not available.");
  }

  // Calculate center-crop coordinates to maintain aspect ratio (cover mode)
  const sourceWidth = img.naturalWidth || img.width;
  const sourceHeight = img.naturalHeight || img.height;

  if (sourceWidth === 0 || sourceHeight === 0) {
    throw new Error("Source image dimensions are invalid.");
  }

  const sourceAspect = sourceWidth / sourceHeight;
  const targetAspect = width / height;

  let sx = 0;
  let sy = 0;
  let sWidth = sourceWidth;
  let sHeight = sourceHeight;

  if (sourceAspect > targetAspect) {
    // Source is wider than target: crop left and right
    sWidth = sourceHeight * targetAspect;
    sx = (sourceWidth - sWidth) / 2;
  } else {
    // Source is taller than target: crop top and bottom
    sHeight = sourceWidth / targetAspect;
    sy = (sourceHeight - sHeight) / 2;
  }

  ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, width, height);

  return canvas.toDataURL(mimeType, quality);
}

/**
 * Generates both the 200x200 standard profile photo and 50x50 icon thumbnail
 * from a selected user image file or blob on Web and native Android/iOS.
 *
 * @param {File | Blob | string} source - The source image (File, Blob, or URI string).
 * @returns {Promise<IProfileImageVariants>} Resized photo and icon Base64 strings.
 */
export async function createProfileImageVariants(
  source: File | Blob | string
): Promise<IProfileImageVariants> {
  if (!source) {
    throw new Error("Image source cannot be null or undefined.");
  }

  // Detect PNG format or default to JPEG
  let targetMimeType: "image/jpeg" | "image/png" = "image/jpeg";
  if (typeof source !== "string" && (source as any).type) {
    if ((source as any).type.toLowerCase() === "image/png") {
      targetMimeType = "image/png";
    }
  } else if (
    typeof source === "string" &&
    (source.startsWith("data:image/png") || source.toLowerCase().endsWith(".png"))
  ) {
    targetMimeType = "image/png";
  }

  if (Platform.OS !== "web") {
    const uri = typeof source === "string" ? source : (source as any).uri;
    const format =
      targetMimeType === "image/png"
        ? ImageManipulator.SaveFormat.PNG
        : ImageManipulator.SaveFormat.JPEG;

    // Calculate center crop to 1:1 aspect ratio if dimensions are obtainable
    const actions: ImageManipulator.Action[] = [];
    try {
      const dimensions = await new Promise<{ width: number; height: number }>((resolve) => {
        RNImage.getSize(
          uri,
          (w, h) => resolve({ width: w, height: h }),
          () => resolve({ width: 0, height: 0 })
        );
      });

      if (dimensions.width > 0 && dimensions.height > 0) {
        const minDim = Math.min(dimensions.width, dimensions.height);
        const originX = Math.round((dimensions.width - minDim) / 2);
        const originY = Math.round((dimensions.height - minDim) / 2);
        actions.push({
          crop: {
            originX,
            originY,
            width: minDim,
            height: minDim,
          },
        });
      }
    } catch {
      // Continue without pre-cropping if getSize fails
    }

    const photoResult = await ImageManipulator.manipulateAsync(
      uri,
      [...actions, { resize: { width: 200, height: 200 } }],
      {
        compress: 0.9,
        format,
        base64: true,
      }
    );

    const iconResult = await ImageManipulator.manipulateAsync(
      uri,
      [...actions, { resize: { width: 50, height: 50 } }],
      {
        compress: 0.85,
        format,
        base64: true,
      }
    );

    return {
      photoBase64: photoResult.base64 || "",
      iconBase64: iconResult.base64 || "",
      contentType: targetMimeType,
    };
  }

  const img = await loadImage(source);

  // Profile photo: 200x200 px
  const photoDataUrl = cropAndResizeImage(img, {
    width: 200,
    height: 200,
    mimeType: targetMimeType,
    quality: targetMimeType === "image/png" ? undefined : 0.9,
  });

  // Small icon: 50x50 px
  const iconDataUrl = cropAndResizeImage(img, {
    width: 50,
    height: 50,
    mimeType: targetMimeType,
    quality: targetMimeType === "image/png" ? undefined : 0.85,
  });

  return {
    photoBase64: photoDataUrl.replace(/^data:[^;]+;base64,/, ""),
    iconBase64: iconDataUrl.replace(/^data:[^;]+;base64,/, ""),
    contentType: targetMimeType,
  };
}
